import { Box, Stack, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import EmptyState from 'features/executive/components/EmptyState/EmptyState'
import ErrorState from 'features/executive/components/ErrorState/ErrorState'
import LoadingSkeleton from 'features/executive/components/LoadingSkeleton/LoadingSkeleton'
import KPICard from 'features/executive/components/KPICard/KPICard'
import KPIDetailRow from 'features/executive/components/KPICard/KPIDetailRow'
import { formatCurrency } from 'utils/executive.utils'
import type { AccountFlowResponse } from 'api/executive/executive.types'

interface AccountFlowCardProps {
  title: string
  inflowLabel: string
  outflowLabel: string
  /** Optional ⓘ tooltip text shown next to the title. */
  tooltip?: string
  /** Optional note appended after the inflow amount (e.g. "12 รายการ"). */
  inflowNote?: string
  /** Optional note appended after the outflow amount. */
  outflowNote?: string
  /** Optional performance-metric rows shown below the inflow/outflow pair. */
  metrics?: { label: string; value: string }[]
  /** Optional labelled breakdown section (e.g. received split by branch).
   *  Rows flagged `sub` render indented + lighter as a child of the row above. */
  breakdown?: { title: string; rows: { label: string; value: string; sub?: boolean }[] }
  data: AccountFlowResponse | null
  loading: boolean
  error: Error | null
  onRetry: () => void
}

export default function AccountFlowCard({
  title,
  inflowLabel,
  outflowLabel,
  tooltip,
  inflowNote,
  outflowNote,
  metrics,
  breakdown,
  data,
  loading,
  error,
  onRetry,
}: AccountFlowCardProps) {
  const { t } = useTranslation()

  if (loading) return <LoadingSkeleton height={140} />
  if (error) return <ErrorState onRetry={onRetry} />

  // Optional ⓘ tooltip rendered to the right of the title.
  const titleInfo = tooltip ? (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
    </Tooltip>
  ) : undefined

  // Backend may report that this ledger isn't implemented (wallet).
  // Show a degraded but informative tile instead of empty/error state.
  if (data && data.supported === false) {
    return (
      <KPICard
        title={title}
        headerAction={titleInfo}
        value="—"
        caption={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.disabled' }} />
            <Tooltip title={data.note || ''} placement="bottom">
              <Typography variant="caption" color="text.disabled">
                ยังไม่เปิดใช้งาน
              </Typography>
            </Tooltip>
          </Box>
        }
      />
    )
  }

  if (!data || (data.inflow === 0 && data.outflow === 0)) return <EmptyState />

  const variance =
    data.previousNetFlow != null && data.previousNetFlow !== 0
      ? (data.netFlow - data.previousNetFlow) / Math.abs(data.previousNetFlow)
      : null

  return (
    <KPICard
      title={title}
      headerAction={titleInfo}
      value={formatCurrency(data.netFlow)}
      caption={
        <Stack spacing={0.5}>
          <KPIDetailRow
            label={t('EXECUTIVE.ACCOUNT.VS_PREVIOUS', 'เทียบช่วงก่อน')}
            variance={variance}
          />
          <KPIDetailRow
            label={inflowNote ? `${inflowLabel} · ${inflowNote}` : inflowLabel}
            value={formatCurrency(data.inflow)}
          />
          <KPIDetailRow
            label={outflowNote ? `${outflowLabel} · ${outflowNote}` : outflowLabel}
            value={formatCurrency(data.outflow)}
          />
          {metrics && metrics.length > 0 && (
            <Box sx={{ pt: 0.5, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {metrics.map((m) => (
                <KPIDetailRow key={m.label} label={m.label} value={m.value} />
              ))}
            </Box>
          )}
          {breakdown && breakdown.rows.length > 0 && (
            <Box sx={{ pt: 0.5, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', fontWeight: 600 }}
              >
                {breakdown.title}
              </Typography>
              {breakdown.rows.map((r, i) => (
                <Box
                  key={`${i}-${r.label}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    pl: r.sub ? 1.5 : 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: r.sub ? 400 : 600 }}
                  >
                    {r.sub ? `· ${r.label}` : r.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={r.sub ? 'text.secondary' : 'text.primary'}
                    sx={{ fontWeight: r.sub ? 400 : 600 }}
                  >
                    {r.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      }
    />
  )
}
